import { useCallback } from 'react';
import { useMedplum } from '@medplum/react';
import { Patient, RelatedPerson } from '@medplum/fhirtypes';
import {
  buildRelatedPersonSubmissionData,
  buildRelatedPersonPatientSubmissionData,
  RelatedPersonType,
} from '../utils/create-patient-helpers';
import { useMedplumSearch } from './useMedplumSearch';

export function useHandleRelatedPersonSubmit() {
  const medplum = useMedplum();
  const { findPersonByAmka } = useMedplumSearch();

  const handleRelatedPersonSubmit = useCallback(
    async (
      editMode: RelatedPerson | null,
      values: Record<string, any>,
      type: RelatedPersonType,
      patient: Patient
    ) => {
      if (editMode) {
        await medplum.updateResource({
          ...editMode,
          ...buildRelatedPersonPatientSubmissionData(values, type),
        });
        return;
      }

      const amkaKey = `${type}Amka`;
      const patientMatch = values[amkaKey]
        ? await findPersonByAmka(values[amkaKey])
        : null;

      const dataRelatedPerson = buildRelatedPersonSubmissionData(
        values,
        type,
        patient
      );

      if (!dataRelatedPerson) {
        return;
      }

      const relatedPerson = await medplum.createResource(dataRelatedPerson);

      const rpPatientData = buildRelatedPersonPatientSubmissionData(
        values,
        type,
        relatedPerson
      );

      if (!patientMatch) {
        return;
      }

      const existingLinks = patientMatch.link ?? [];

      const updatedPatient: Patient = {
        ...patientMatch,
        link: [
          ...existingLinks,
          {
            other: { reference: `RelatedPerson/${relatedPerson.id}` },
            type: 'seealso',
          },
        ],
      };

      await medplum.updateResource({ ...updatedPatient, ...rpPatientData });
    },
    [medplum, findPersonByAmka]
  );

  return { handleRelatedPersonSubmit };
}
